import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ApiError, decideDeviceGrant, fetchDeviceGrant, signInURL } from "../api";
import Placeholder, { Pending } from "../components/Placeholder";
import { useT } from "../i18n";
import { useSession } from "../session";

/**
 * DevicePage approves a command-line sign-in (ADR-0142): the CLI prints a link
 * with its code, and the owner opens it on the phone. It shows what is asking
 * and when, and one Approve and one Deny — nothing else, because a phished
 * code is approved here or nowhere.
 */
export default function DevicePage() {
  const t = useT();
  const { me, isPending } = useSession();
  const [params] = useSearchParams();
  const code = params.get("code") ?? "";
  const [answer, setAnswer] = useState<"approved" | "denied" | null>(null);

  const grant = useQuery({
    queryKey: ["device-grant", code],
    queryFn: () => fetchDeviceGrant(code),
    enabled: !!me && !!code && answer === null,
    retry: false,
  });
  const decide = useMutation({
    mutationFn: (approve: boolean) => decideDeviceGrant(code, approve),
    onSuccess: (_, approve) => setAnswer(approve ? "approved" : "denied"),
  });

  const minutes = grant.data
    ? Math.max(0, Math.floor((Date.now() - new Date(grant.data.requested_at).getTime()) / 60000))
    : 0;
  const missing = grant.error instanceof ApiError && grant.error.status === 404;

  return (
    <main className="mx-auto max-w-md px-4 py-10">
      <header className="mb-6">
        <h1 className="text-[1.75rem] leading-tight font-semibold tracking-tight">
          {t.device.title}
        </h1>
        <p className="mt-1 text-sm text-muted">{t.device.what}</p>
      </header>

      {isPending && (
        <Pending label={t.common.loading}>
          <Placeholder className="h-32 rounded-card" />
        </Pending>
      )}

      {!isPending && !me && (
        <a
          href={signInURL(window.location.pathname + window.location.search)}
          className="inline-flex rounded-control bg-sage/15 px-3.5 py-2 text-sm font-medium text-sage-deep hover:bg-sage/25"
        >
          {t.device.signIn}
        </a>
      )}

      {me && !code && <p className="text-sm text-muted">{t.device.noCode}</p>}

      {answer && (
        <p data-testid="device-answer" role="status" className="text-sm">
          {answer === "approved" ? t.device.approved : t.device.denied}
        </p>
      )}

      {me && code && !answer && grant.isPending && (
        <Pending label={t.common.loading}>
          <Placeholder className="h-32 rounded-card" />
        </Pending>
      )}

      {me && code && !answer && grant.isError && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {missing ? t.device.notFound : grant.error.message}
        </p>
      )}

      {grant.data && !answer && (
        <section
          data-testid="device-grant"
          className="rounded-card border border-line bg-surface p-4"
        >
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-xs text-muted">{t.device.code}</dt>
              <dd>
                <bdi className="font-mono text-lg tracking-widest">{grant.data.user_code}</bdi>
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted">{t.device.asking}</dt>
              {/* Untrusted text the CLI sent: rendered as text, its direction
                  resolved from its own content. */}
              <dd dir="auto" className="break-words">
                {grant.data.label}
              </dd>
            </div>
          </dl>
          <p className="mt-3 text-xs text-muted">{t.device.requested(minutes)}</p>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              data-testid="device-approve"
              disabled={decide.isPending}
              onClick={() => decide.mutate(true)}
              className="rounded-control bg-sage/15 px-3.5 py-2 text-sm font-medium text-sage-deep hover:bg-sage/25 disabled:opacity-50"
            >
              {t.device.approve}
            </button>
            <button
              type="button"
              data-testid="device-deny"
              disabled={decide.isPending}
              onClick={() => decide.mutate(false)}
              className="rounded-control bg-sunk px-3.5 py-2 text-sm text-ink-soft hover:bg-sunk/70 disabled:opacity-50"
            >
              {t.device.deny}
            </button>
          </div>
          {decide.isError && (
            <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">
              {t.device.failed} {decide.error.message}
            </p>
          )}
        </section>
      )}
    </main>
  );
}
